import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LoadingStatusWrapper, PopOutChart, InfoMessage, GeneralButton } from 'sharedComponents';
import { useTranslation } from 'react-i18next';
import useChartConfig from './useChartConfig';
import { chartModules, default3DOptions } from '../constants';
import '../style.scss';
import './style.scss';
import {
    ChartViewToggleButton,
    EchartDownloadButton,
    PopOutButton,
    withLeftButtonGroup,
    withPopOutRow,
    withRightButtonGroup,
} from 'sharedComponents/Chart/headers';
import FFTHistoricalTimestampDialog from '../FFTHistoricalTimestampDialog';
import useChartResize from 'sharedHooks/useChartResize';
import EchartLeftHeaderOptions from './EchartLeftHeaderOptions';
import EchartRightPanel from './EchartRightPanel';
import EchartHelpInfo from './EchartHelpInfo';
import EchartTimestampRemove from './EchartTimestampRemove';
import { BEARING_TYPES } from 'sharedComponents/Chart/constants';
import { echartLegendNames } from './constants';
import { assetType } from 'helpers/constants';


const VibrationFFTEchart3DComponent = (props) => {

    const { t: translate } = useTranslation();
    const { model, actions, selectedTimestamps } = props;
    const containerRef = useRef(null);
    const [chartInstance, setChartInstance] = useState(null);
    const [showHistoricalDialog, setShowHistoricalDialog] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const chartInstanceRef = useRef(null);
    const chartRef = useRef(null);
    const chart2DRef = useRef(null);
    const fftData = false;
    const isMSRasset = model.sensorType === assetType.MACHSENSOR;
    const [chartConfig, chart2DConfig, on2DEvents, downloadChart] = useChartConfig(
        model.fftData,
        model.selectedEchartView,
        model.selectedEchartLegend,
        model.selectedEchartBandSettings,
        model.sensorType,
        selectedTimestamps,
        chartInstance,
        chart2DRef,
        model.frequencyRange,
        actions.setEchartFrequencyRange,
        model.selectedAsset,
    );

    useEffect(() => {
        if (isMSRasset) {
            actions.setEchartBandSettings({
                ...model.selectedEchartBandSettings,
                bearingTypes: [BEARING_TYPES.DE]
            });
            actions.setSelectedEchartLegend(echartLegendNames.msrAsset[0]);
        } else {
            actions.setEchartBandSettings({
                ...model.selectedEchartBandSettings,
                bearingTypes: [BEARING_TYPES.DE, BEARING_TYPES.NDE]
            });
            actions.setSelectedEchartLegend(echartLegendNames.sensorAsset[0]);
        }
    }, []);

    useEffect(() => {
        const checkWidth = () => {
            if (containerRef?.current) {
                setIsSticky(containerRef.current.offsetWidth < 1500);
            }
        };

        checkWidth();

        if (containerRef?.current) {
            const resizeObserver = new ResizeObserver(checkWidth);
            resizeObserver.observe(containerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [containerRef]);

    useEffect(() => {
        chartInstanceRef.current = chartInstance;
        if (chartRef.current) {
            chartRef.current.chart = chartInstance;
        }
    }, [chartInstance]);

    useChartResize(containerRef, chartRef);

    const historicalTimeStamp = (props) => {
        const { mainChartInst } = props;
        return mainChartInst ?
            <div className='echart-left-header'>
                <div key='fft-historical' className='historical-timestamp'>
                    <GeneralButton
                        type='normal'
                        text={`${translate('ABB.Powertrain.Frontend.vibrationFFTSelectTimestamp')}${selectedTimestamps.length ? ` (${selectedTimestamps.length})` : ''}`}
                        onClick={() => setShowHistoricalDialog(true)}
                    />
                </div>
                <EchartLeftHeaderOptions {...props} />
            </div> : null;
    };

    const MainChartHeader = withPopOutRow([
        withLeftButtonGroup([historicalTimeStamp]),
        withRightButtonGroup([ChartViewToggleButton, EchartDownloadButton, PopOutButton])
    ]);

    const PopOutChartHeader = withPopOutRow([
        withLeftButtonGroup([EchartLeftHeaderOptions]),
        withRightButtonGroup([EchartDownloadButton, PopOutButton])
    ]);

    return (
        <div
            ref={containerRef}
            style={{ height: '100%', position: 'relative' }}
            className={`vibration-fft-echart-3d-component${isSticky ? ' sticky-top-panel' : ''}`}
        >
            <LoadingStatusWrapper
                useCustomAnchor
                transparent
                indicatorConfig={{ placement: 'sticky' }}
                loadingStatus={[model?.fftDataLoadingStatus]}
            >
                <PopOutChart
                    hasKpi={model?.hasRawData}
                    data={fftData}
                    renderNoKpi={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.noDataLabelAvailable'
                        text='ABB.Powertrain.Frontend.collectRawDataLabel' />}
                    selectedTimestamps={selectedTimestamps}
                    loadingStatus={model?.fftDataLoadingStatus}
                    options={chartConfig}
                    options2D={chart2DConfig}
                    modules={chartModules}
                    container='op-fft-chart-v2'
                    mainChartHeader={MainChartHeader}
                    popOutChartHeader={PopOutChartHeader}
                    isEchart3D={true}
                    onChartReady={setChartInstance}
                    chartInstance={chartInstance}
                    default3DOptions={default3DOptions}
                    selectedChartView={model.selectedChartView}
                    setSelectedChartView={actions.setSelectedChartView}
                    selectedXAxisUnit={model.selectedXAxisUnit}
                    setSelectedXAxisUnit={actions.setSelectedXAxisUnit}
                    setChartLoader={actions.setChartLoader}
                    setSelectedEchartView={actions.setSelectedEchartView}
                    selectedEchartView={model.selectedEchartView}
                    selectedEchartLegend={model.selectedEchartLegend}
                    setSelectedEchartLegend={actions.setSelectedEchartLegend}
                    on2DEvents={on2DEvents}
                    sensorType={model.sensorType}
                    downloadChart={downloadChart}
                    chart2DRef={chart2DRef}
                    popOutModalClassName={'echart-3d-popout-modal'}
                    popOutClassName={'vibration-fft-echart-3d-popout'}
                    echartPanels={
                        <>
                            <EchartRightPanel
                                setEchartBandSettings={actions.setEchartBandSettings}
                                selectedEchartBandSettings={model.selectedEchartBandSettings}
                                sensorType={model.sensorType}
                                selectedEchartView={model.selectedEchartView}
                            />
                            <EchartHelpInfo />
                        </>
                    }
                    popOutFooter={
                        <EchartTimestampRemove
                            selectedTimestamps={selectedTimestamps}
                            setSelectedTimestamps={actions.setSelectedTimestamps}
                            selectedAsset={model.selectedAsset}
                            selectedOrganization={model.selectedOrganization}
                            getFFTDataByTimestamp={actions.getFFTDataByTimestamp}
                            fftData={model.fftData}
                        />
                    }
                />

                {showHistoricalDialog &&
                    <FFTHistoricalTimestampDialog
                        showHistoricalDialog={showHistoricalDialog}
                        setShowHistoricalDialog={setShowHistoricalDialog}
                        setSelectedDates={actions.setSelectedDates}
                        dateSelection={model.dateSelection}
                        getFFTTimestampList={actions.getFFTTimestampList}
                        fftTimestampList={model.fftTimestampList}
                        getFFTDataByTimestamp={actions.getFFTDataByTimestamp}
                        fftDataLoadingStatus={model?.fftDataLoadingStatus}
                        selectedTimestamps={selectedTimestamps}
                        setSelectedTimestamps={actions.setSelectedTimestamps}
                        selectedOrganization={model.selectedOrganization}
                        selectedAsset={model.selectedAsset}
                        setSelectedLegends={actions.setSelectedLegends}
                        sensorType={model.sensorType}
                    />
                }

            </LoadingStatusWrapper>
        </div>
    );
};

VibrationFFTEchart3DComponent.propTypes = {
    actions: PropTypes.shape({
        getFFTTimestampList: PropTypes.func,
        getFFTDataByTimestamp: PropTypes.func,
        setSelectedDates: PropTypes.func,
        setSelectedTimestamps: PropTypes.func,
        setChartLoader: PropTypes.func,
        setSelectedChartView: PropTypes.func,
        setSelectedXAxisUnit: PropTypes.func,
        setSelectedEchartView: PropTypes.func,
        setSelectedEchartLegend: PropTypes.func,
        setEchartBandSettings: PropTypes.func,
        setEchartFrequencyRange: PropTypes.func,
        setSelectedLegends: PropTypes.func,
    }),

    model: PropTypes.shape({
        fftData: PropTypes.arrayOf(PropTypes.object),
        fftDataLoadingStatus: PropTypes.string,
        measurementExportLoadingStatus: PropTypes.string,
        trendDataLoadingStatus: PropTypes.string,
        dateSelection: PropTypes.object,
        fftTimestampList: PropTypes.object,
        speed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sensorTypeID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sensorType: PropTypes.string,
        hasRawData: PropTypes.bool,
        selectedAsset: PropTypes.object,
        selectedOrganization: PropTypes.object,
        selectedChartView: PropTypes.string,
        selectedXAxisUnit: PropTypes.string,
        selectedEchartView: PropTypes.string,
        selectedEchartLegend: PropTypes.string,
        selectedEchartBandSettings: PropTypes.object
    }).isRequired
};

export default VibrationFFTEchart3DComponent;
